import React, { useState } from "react";
import { supabase } from "@foundation/utils/supabaseClient";
import { Loader2, MailCheck, Info } from "lucide-react";
import { Button } from "@foundation/components/ui/button";
import { Input } from "@foundation/components/ui/input";
import { Label } from "@foundation/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@foundation/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@foundation/components/ui/dialog";

type MagicLinkButtonProps = {
  // Disable the trigger button (e.g. while another auth action is in flight)
  disabled?: boolean;
  // Metadata attached to the user on signup, e.g. { full_name: '...' }
  userData?: Record<string, unknown>;
  // Label shown on the trigger button
  buttonText?: string;
  // Heading shown at the top of the modal
  modalTitle?: string;
  // Tooltip copy shown next to the modal heading
  tooltipText?: string;
};

const DEFAULT_BUTTON_TEXT = "Email me a magic sign-in link";

const DEFAULT_MODAL_TITLE = "Sign in with a magic link";

const DEFAULT_TOOLTIP =
  "A magic link lets you sign in without a password. We'll email you a secure link — click it on this device to sign in instantly.";

export const MagicLinkButton = ({
  disabled = false,
  userData,
  buttonText = DEFAULT_BUTTON_TEXT,
  modalTitle = DEFAULT_MODAL_TITLE,
  tooltipText = DEFAULT_TOOLTIP,
}: MagicLinkButtonProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const openModal = () => {
    setEmail("");
    setError("");
    setSent(false);
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          // Return to the app root; the Supabase client detects the session in
          // the URL and the auto-login effect routes the user onward.
          emailRedirectTo: window.location.origin,
          ...(userData ? { data: userData } : {}),
        },
      });

      if (error) throw error;

      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send magic link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        className="w-full text-primary"
        onClick={openModal}
        disabled={disabled}
      >
        {buttonText}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          {sent ? (
            <>
              <DialogHeader>
                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <MailCheck className="w-6 h-6 text-primary" />
                </div>
                <DialogTitle className="text-center">Check your email</DialogTitle>
                <DialogDescription className="text-center">
                  We've emailed a magic sign-in link to{" "}
                  <span className="font-medium text-foreground">{email}</span>.
                  Open it on this device to sign in instantly.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => setOpen(false)}
                >
                  Got it
                </Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-1.5">
                  {modalTitle}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="What is a magic link?"
                          className="text-muted-foreground hover:text-primary"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        {tooltipText}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </DialogTitle>
                <DialogDescription>
                  Enter your email and we'll send you a secure link to sign in —
                  no password needed.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2 py-4">
                {error && (
                  <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30 text-destructive text-sm">
                    {error}
                  </div>
                )}
                <Label htmlFor="magic-link-email">Email address</Label>
                <Input
                  id="magic-link-email"
                  type="email"
                  placeholder="name@example.com"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <DialogFooter>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? "Sending link..." : "Send magic link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
